import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { getApiUrl, getSettings, updateSettings, uploadSiteFavicon } from "../utils/apiClient";
import { useSiteSettings } from "../context/SiteSettingsContext";

function SiteSettingsPanel() {
  const { refreshSettings } = useSiteSettings();
  const [siteIdentity, setSiteIdentity] = useState("Søren Kølbæk");
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [faviconError, setFaviconError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getSettings();
        if (cancelled) return;
        setSiteIdentity(data?.site_identity || "Søren Kølbæk");
        setFaviconUrl(data?.favicon_url || null);
      } catch (err) {
        if (!cancelled) {
          const detail = err.response?.data?.detail;
          setError(typeof detail === "string" ? detail : err.message || "Kunne ikke indlæse indstillinger");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const data = await updateSettings({ site_identity: siteIdentity.trim() });
      setSiteIdentity(data?.site_identity || siteIdentity.trim());
      setFaviconUrl(data?.favicon_url || faviconUrl);
      await refreshSettings();
      setSaved(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke gemme");
    } finally {
      setSaving(false);
    }
  };

  const handleFaviconChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    setFaviconError("");
    setSaved(false);
    try {
      const data = await uploadSiteFavicon(file);
      setFaviconUrl(data?.favicon_url || null);
      await refreshSettings();
      setSaved(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setFaviconError(typeof detail === "string" ? detail : err.message || "Upload mislykkedes");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const faviconPreview = faviconUrl
    ? faviconUrl.startsWith("http")
      ? faviconUrl
      : `${getApiUrl()}${faviconUrl}`
    : `${process.env.PUBLIC_URL || ""}/favicon.ico`;

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
        Site Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Site identity bruges i navigation og browsertitel. Favicon vises i fanen og bogmærker.
      </Typography>

      <Stack spacing={3}>
        <TextField
          label="Site identity"
          value={siteIdentity}
          onChange={(e) => {
            setSiteIdentity(e.target.value);
            setSaved(false);
          }}
          fullWidth
          inputProps={{ maxLength: 200 }}
          helperText="Vises som fx “Søren Kølbæk - Opskriftstitel” i browsertitel."
        />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Favicon
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              component="img"
              src={faviconPreview}
              alt=""
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                objectFit: "contain",
                bgcolor: "#fff",
              }}
            />
            <Button variant="outlined" component="label" disabled={uploading}>
              {uploading ? "Uploader…" : "Upload favicon"}
              <input
                hidden
                type="file"
                accept="image/x-icon,image/png,image/jpeg,image/webp,image/gif,.ico"
                onChange={handleFaviconChange}
              />
            </Button>
          </Stack>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.75 }}>
            ICO, PNG, JPEG, WebP eller GIF. Erstatter det nuværende favicon.
          </Typography>
          {faviconError ? (
            <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
              {faviconError}
            </Typography>
          ) : null}
        </Box>

        <Box>
          <Button variant="contained" onClick={handleSave} disabled={saving || !siteIdentity.trim()}>
            {saving ? "Gemmer…" : "Gem site identity"}
          </Button>
          {saved ? (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              Gemt.
            </Typography>
          ) : null}
          {error ? (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}

export default SiteSettingsPanel;
